import { useEffect, useMemo, useState } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getStripe } from '~/lib/stripe';
import { friendlyStripeError } from '~/lib/stripeErrors';
import { apiCreateSetupIntent, apiSetDefaultPaymentMethod } from '~/lib/api/billing';
import { useNotify } from '~/components/account/ModalNotifications';
import { AccessibleModal } from '~/components/account/AccessibleModal';

function extractApiError(err: any, fallback: string): string {
  // Log the raw error for debugging; never surface axios/backend technical text to users.
  console.error(err);

  const apiMessage = err?.response?.data?.message;

  return (Array.isArray(apiMessage) ? apiMessage.join(', ') : apiMessage) || fallback;
}

// Stripe Elements styled to match the dark editorial system.
const ELEMENTS_APPEARANCE = {
  theme: 'night' as const,
  variables: {
    colorPrimary: '#D9F26A',
    colorBackground: '#16161B',
    colorText: '#EDEDF0',
    colorDanger: '#F26A6A',
    fontFamily: 'Geist, system-ui, sans-serif',
    borderRadius: '8px',
    spacingUnit: '3px',
  },
};

interface PaymentMethodModalProps {
  publishableKey: string;
  hasExistingCard: boolean;
  onClose: () => void;
}

/**
 * In-house "update payment method" flow. Collects a card via Stripe Elements
 * (no card data touches our servers), confirms a SetupIntent, then makes it the
 * default for future invoices. Everything happens inside Dunea.
 */
export function PaymentMethodModal({ publishableKey, hasExistingCard, onClose }: PaymentMethodModalProps) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [setupError, setSetupError] = useState<string | null>(null);
  const stripePromise = useMemo(() => getStripe(publishableKey), [publishableKey]);

  // Create the SetupIntent once when the modal opens.
  useEffect(() => {
    let cancelled = false;

    apiCreateSetupIntent()
      .then(({ clientSecret }) => {
        if (!cancelled) {
          setClientSecret(clientSecret);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setSetupError(extractApiError(err, 'Could not update your card. Please try again.'));
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const heading = hasExistingCard ? 'Update payment method' : 'Add payment method';

  return (
    <AccessibleModal
      title={heading}
      description="Your card is secured by Stripe and never touches our servers."
      maxWidth={440}
      onClose={onClose}
    >
      <div className="mb-1 flex items-center gap-2.5">
        <span className="i-ph:credit-card text-[18px] text-accent" />
        <h3 className="ui-serif m-0 text-[20px]">{heading}</h3>
      </div>
      <p className="m-0 mb-4.5 text-[12px] leading-normal text-muted">
        Your card is secured by Stripe and never touches our servers.
      </p>

      {setupError ? (
        <div className="mb-4 text-[12.5px] text-danger">{setupError}</div>
      ) : !clientSecret ? (
        <div className="flex items-center gap-2 py-6 text-muted">
          <span className="i-svg-spinners:90-ring-with-bg" />
          <span className="text-[12.5px]">Preparing secure form…</span>
        </div>
      ) : (
        <Elements stripe={stripePromise} options={{ clientSecret, appearance: ELEMENTS_APPEARANCE }}>
          <CardForm onClose={onClose} />
        </Elements>
      )}

      {(setupError || !clientSecret) && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
          <button onClick={onClose} className="ui-btn-ghost">
            Close
          </button>
        </div>
      )}
    </AccessibleModal>
  );
}

/** The Elements-scoped inner form; needs the Stripe + Elements context. */
function CardForm({ onClose }: { onClose: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const queryClient = useQueryClient();
  const notify = useNotify();
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!stripe || !elements) {
        throw new Error('Payment form is still loading. Please try again.');
      }

      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
      });

      if (confirmError) {
        throw new Error(
          friendlyStripeError(confirmError, 'Your card could not be saved. Please check the details and try again.'),
        );
      }

      const paymentMethodId =
        typeof setupIntent?.payment_method === 'string' ? setupIntent.payment_method : setupIntent?.payment_method?.id;

      if (!paymentMethodId) {
        throw new Error('Your card could not be saved. Please try again.');
      }

      return apiSetDefaultPaymentMethod(paymentMethodId);
    },
    onSuccess: () => {
      notify.success('Card updated');
      queryClient.invalidateQueries({ queryKey: ['billing-overview'] });
      onClose();
    },
    onError: (err: any) => {
      setError(extractApiError(err, 'Your card could not be saved.'));
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        saveMutation.mutate();
      }}
    >
      <PaymentElement onReady={() => setReady(true)} />

      {error && <div className="mt-3 text-[12px] text-danger">{error}</div>}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
        <button type="button" onClick={onClose} className="ui-btn-ghost" disabled={saveMutation.isPending}>
          Cancel
        </button>
        <button
          type="submit"
          className="ui-btn-accent"
          disabled={!ready || !stripe || saveMutation.isPending}
          style={{ opacity: !ready || saveMutation.isPending ? 0.6 : 1 }}
        >
          {saveMutation.isPending ? 'Saving…' : 'Save card'}
        </button>
      </div>
    </form>
  );
}
